import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/** Surface container. `interactive` adds hover affordance for clickable cards. */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { readonly interactive?: boolean }>(
  ({ className, interactive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-lg border border-border bg-surface text-text shadow-sm',
        interactive && 'transition-colors duration-fast hover:border-border-strong/60',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

export const CardHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-start justify-between gap-3 p-5 pb-3', className)} {...props} />
);
export const CardTitle = ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className={cn('text-base font-semibold leading-tight text-text', className)} {...props} />
);
export const CardDescription = ({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('mt-1 text-sm text-text-muted', className)} {...props} />
);
export const CardContent = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-5 pb-5', className)} {...props} />
);
export const CardFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center gap-2 border-t border-border px-5 py-3', className)} {...props} />
);

export const badgeVariants = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium [&_svg]:size-3',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-sunken text-text-muted',
        primary: 'bg-primary-soft text-primary',
        positive: 'bg-positive-soft text-positive',
        negative: 'bg-negative-soft text-negative',
        warning: 'bg-warning-soft text-warning',
        info: 'bg-info-soft text-info',
        accent: 'bg-accent-soft text-text',
        solid: 'bg-primary text-primary-foreground',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);
export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}
/** Small status label. Never rely on colour alone — the text must carry the meaning. */
export const Badge = ({ className, tone, ...props }: BadgeProps) => <span className={cn(badgeVariants({ tone }), className)} {...props} />;

/** Keyboard key cap. */
export const Kbd = ({ className, ...props }: HTMLAttributes<HTMLElement>) => (
  <kbd
    className={cn(
      // keyboard hints mean nothing on touch screens
      'inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-surface-sunken px-1 font-sans text-[11px] font-medium text-text-muted coarse:!hidden',
      className,
    )}
    {...props}
  />
);

/** Loading placeholder that reserves layout space. */
export const Skeleton = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div aria-hidden className={cn('animate-pulse rounded-md bg-surface-sunken', className)} {...props} />
);

export interface StatProps {
  readonly label: ReactNode;
  readonly value: ReactNode;
  /** e.g. `<DeltaBadge>` */
  readonly delta?: ReactNode;
  readonly hint?: ReactNode;
  readonly icon?: ReactNode;
  readonly className?: string;
}

/** Labelled figure tile for summaries (label above, big tabular value, optional delta). */
export const Stat = ({ label, value, delta, hint, icon, className }: StatProps) => (
  <div className={cn('rounded-lg border border-border bg-surface p-4', className)}>
    <div className="flex items-center gap-2 text-sm text-text-muted">
      {icon ? <span className="text-text-muted [&_svg]:size-4" aria-hidden>{icon}</span> : null}
      <span>{label}</span>
    </div>
    <div className="mt-2 flex flex-wrap items-baseline gap-2">
      <span className="text-2xl font-semibold tabular-nums tracking-tight text-text">{value}</span>
      {delta}
    </div>
    {hint ? <p className="mt-1 text-xs text-text-muted">{hint}</p> : null}
  </div>
);
